import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import {
  FaArrowRight,
  FaCheck,
  FaChevronRight,
  FaCloudUploadAlt,
  FaInfoCircle,
  FaPeopleCarry,
  FaUser,
  FaUsers
} from "react-icons/fa";
import {
  claimClassroomTransitionOwnership,
  getClassesBySchoolId,
  getClassroomTransitionPreflight,
  getTeacherAccountLookup,
  getTeachersBySchoolId,
  saveClassroomTransitionScope
} from "../services/TeacherAPIClient";
import {
  getClassroomTransitionRows,
  normaliseRosterName
} from "../utils/classroomTransitionRoster";
import TeacherClassroomPopulationWorkspace from "./TeacherClassroomPopulationWorkspace";
import TeacherClassroomReconciliation from "./TeacherClassroomReconciliation";
import TeacherClassroomReviewConfirm from "./TeacherClassroomReviewConfirm";
import "./TeacherClassroomTransitionModal.css";

const ALLOWED_STATES = new Set(["eligible", "completed", "ineligible"]);
const DEVELOPMENT_MOCK_PARAMETER = "classroomTransitionMock";
const DEVELOPMENT_SCOPE_POINTS = [
  {
    id: "level-one-to-two",
    sourceClassLevelName: "Level 1",
    targetClassLevelName: "Level 2"
  },
  {
    id: "level-two-to-three",
    sourceClassLevelName: "Level 2",
    targetClassLevelName: "Level 3"
  }
];
const DEVELOPMENT_CHILDREN = [
  "Aisha Khan",
  "Aoife Byrne",
  "Ben Healy",
  "Chloe Kelly",
  "Daniel Lynch",
  "Ella O’Connor",
  "Emma Farrell",
  "Fionn Gallagher",
  "Grace Walsh",
  "Jamie Murphy",
  "Lily O’Neill",
  "Noah O’Connor",
  "Olivia Dunne",
  "Patrick O’Brien",
  "Zara Walsh"
].map((name, index) => ({ id: `development-child-${index + 1}`, name }));

const DEVELOPMENT_KNOWN_SCOPE_RESPONSE = {
  status: "eligible",
  scopeStatus: "known",
  progressionPoints: DEVELOPMENT_SCOPE_POINTS.map((point, index) => ({
    ...point,
    expectedClassroomCount: index === 0 ? 4 : 5,
    confirmedCount: index === 0 ? 1 : 0,
    confirmedClassrooms: index === 0 ? [{ id: "si-room-1", name: "SI Room 1" }] : [],
    availableChildCount: index === 0 ? 125 : 88,
    availableChildren: DEVELOPMENT_CHILDREN
  })),
  classes: [{ id: "si-room-1", name: "SI Room 1", academicYear: "2026/27" }],
  teachers: [
    { teacherid: "teacher-mary", name: "Mary Murphy", email: "mary@example.test", status: "active" },
    { pendingteacherid: "teacher-pat", name: "Pat Pending", email: "pat@example.test", status: "invitation sent" }
  ]
};

const DEVELOPMENT_MOCK_RESPONSES = {
  eligible: DEVELOPMENT_KNOWN_SCOPE_RESPONSE,
  "scope-known": DEVELOPMENT_KNOWN_SCOPE_RESPONSE,
  "scope-unknown": {
    status: "eligible",
    scopeStatus: "unknown",
    progressionPoints: DEVELOPMENT_SCOPE_POINTS
  },
  "scope-partial": {
    status: "eligible",
    scopeStatus: "partial",
    progressionPoints: DEVELOPMENT_SCOPE_POINTS.map((point, index) => ({
      ...point,
      selected: true,
      expectedClassroomCount: index === 0 ? 4 : null
    }))
  },
  completed: {
    status: "completed",
    classLevels: [
      { id: "level-2", name: "Level 2", confirmedCount: 4, expectedCount: 4 },
      { id: "level-3", name: "Level 3", confirmedCount: 5, expectedCount: 5 }
    ]
  },
  ineligible: { status: "ineligible" }
};

const PENDING_TEACHER_STATUSES = new Set([
  "awaiting setup",
  "invitation sent",
  "invite sent",
  "invited",
  "pending",
  "pending teacher",
  "setup incomplete",
  "setup not completed"
]);

const normaliseComparableText = (value) => normaliseRosterName(value).toLowerCase();

const firstPresentValue = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  ) || "";

const normaliseTeacher = (teacher) => {
  const pendingTeacherId = firstPresentValue(
    teacher?.pendingteacherid,
    teacher?.pendingTeacherId,
    teacher?.pending_teacher_id,
    teacher?.pendingTeacher?.id,
    teacher?.pending_teacher?.id
  );
  const teacherId = firstPresentValue(
    teacher?.teacherid,
    teacher?.teacherId,
    teacher?.teacher_id,
    teacher?.teacher?.id,
    !pendingTeacherId ? teacher?.id || teacher?._id : ""
  );
  const status = normaliseComparableText(
    firstPresentValue(
      teacher?.status,
      teacher?.accountStatus,
      teacher?.account_status,
      teacher?.inviteStatus,
      teacher?.invite_status
    )
  ).replace(/[-_]/g, " ");
  const kind = pendingTeacherId || PENDING_TEACHER_STATUSES.has(status)
    ? "pending"
    : "active";
  const email = firstPresentValue(
    teacher?.email,
    teacher?.teacherEmail,
    teacher?.teacher_email,
    teacher?.pendingEmail,
    teacher?.pending_email,
    teacher?.pendingTeacher?.email,
    teacher?.pending_teacher?.email
  );
  const name = normaliseRosterName(
    teacher?.name ||
    teacher?.fullName ||
    teacher?.displayName ||
    [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ")
  );
  const id = firstPresentValue(pendingTeacherId, teacherId, teacher?.id, teacher?._id, email);

  return {
    id,
    kind,
    name: name || email || "Unnamed teacher",
    email,
    teacherId: kind === "active" ? teacherId : "",
    pendingTeacherId: kind === "pending" ? pendingTeacherId || id : ""
  };
};

const mergeTeacherRows = (...groups) => {
  const teachersByIdentity = new Map();

  groups.flat().forEach((teacher) => {
    const normalisedTeacher = normaliseTeacher(teacher);
    const key = normaliseComparableText(normalisedTeacher.email) || normalisedTeacher.id;
    if (!key) return;

    const existing = teachersByIdentity.get(key);
    teachersByIdentity.set(key, existing?.kind === "active" ? existing : normalisedTeacher);
  });

  return Array.from(teachersByIdentity.values()).sort((left, right) =>
    left.name.localeCompare(right.name)
  );
};

const getClassName = (classItem) =>
  normaliseRosterName(classItem?.name || classItem?.className);

const getClassAcademicYear = (classItem) => firstPresentValue(
  classItem?.academicYear,
  classItem?.academic_year,
  classItem?.academicyear,
  classItem?.schoolYear,
  classItem?.yearLabel
);

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normaliseRosterName(email));

export function getClassroomTransitionAcademicYear(date = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-IE", {
      day: "numeric",
      month: "numeric",
      timeZone: "Europe/Dublin",
      year: "numeric"
    })
      .formatToParts(date)
      .filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, Number(value)])
  );
  const startYear = parts.month > 8 || (parts.month === 8 && parts.day >= 1)
    ? parts.year
    : parts.year - 1;

  return `${startYear}/${String(startYear + 1).slice(-2)}`;
}

export function getClassroomTransitionDevelopmentMock(
  search = typeof window === "undefined" ? "" : window.location.search,
  nodeEnv = process.env.NODE_ENV
) {
  if (nodeEnv !== "development") {
    return null;
  }

  const mockStatus = new URLSearchParams(search).get(DEVELOPMENT_MOCK_PARAMETER);
  if (DEVELOPMENT_MOCK_RESPONSES[mockStatus]) {
    return Promise.resolve(DEVELOPMENT_MOCK_RESPONSES[mockStatus]);
  }
  if (mockStatus === "unauthorized") {
    return Promise.reject({ response: { status: 403 } });
  }
  if (mockStatus === "error") {
    return Promise.reject(new Error("Classroom Transition development mock error"));
  }

  return null;
}

export function isClassroomTransitionDevelopmentMock(
  search = typeof window === "undefined" ? "" : window.location.search,
  nodeEnv = process.env.NODE_ENV
) {
  const mockStatus = new URLSearchParams(search).get(DEVELOPMENT_MOCK_PARAMETER);
  return nodeEnv === "development" && Boolean(DEVELOPMENT_MOCK_RESPONSES[mockStatus]);
}

const normaliseClassLevels = (classLevels) =>
  (Array.isArray(classLevels) ? classLevels : [])
    .map((classLevel, index) => ({
      ...classLevel,
      id: classLevel?.id || classLevel?.progressionPointId || `class-level-${index}`,
      name: classLevel?.name ||
        classLevel?.targetClassLevelName ||
        classLevel?.classLevelName ||
        classLevel?.label ||
        "Class level",
      confirmedCount: Number(classLevel?.confirmedCount ?? classLevel?.completedCount ?? 0),
      expectedCount: Number(
        classLevel?.expectedCount ??
        classLevel?.expectedClassroomCount ??
        classLevel?.classroomCount ??
        0
      )
    }))
    .filter((classLevel) => classLevel.expectedCount > 0);

const normaliseCount = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "";
  }

  const count = Number(value);
  return Number.isFinite(count) ? count : "";
};

const slugify = (value, fallback) =>
  String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const normaliseProgressionPoints = (preflight) => {
  const rawPoints = Array.isArray(preflight?.progressionPoints)
    ? preflight.progressionPoints
    : Array.isArray(preflight?.availableProgressionPoints)
      ? preflight.availableProgressionPoints
      : Array.isArray(preflight?.classLevels)
        ? preflight.classLevels
        : [];

  return rawPoints.map((point, index) => {
    const targetClassLevelName = String(
      point?.targetClassLevelName ||
      point?.targetClassLevel ||
      point?.classLevelName ||
      point?.name ||
      point?.label ||
      "Class level"
    ).trim();
    const sourceClassLevelName = String(
      point?.sourceClassLevelName || point?.sourceClassLevel || point?.previousClassLevelName || ""
    ).trim();
    const expectedClassroomCount = normaliseCount(
      point?.expectedClassroomCount ?? point?.expectedCount ?? point?.classroomCount
    );
    const confirmedClassrooms = getClassroomTransitionRows(
      point?.confirmedClassrooms || point?.completedClassrooms,
      ["classrooms", "rows"]
    );
    const draftClassrooms = getClassroomTransitionRows(
      point?.draftClassrooms || point?.classroomDrafts,
      ["classrooms", "drafts", "rows"]
    );
    const currentDraft = point?.currentDraft || point?.currentTeacherDraft || null;
    const confirmedCount = Math.max(
      0,
      Number(point?.confirmedCount ?? point?.completedCount ?? confirmedClassrooms.length) || 0
    );
    const id = String(
      point?.id ||
      point?.progressionPointId ||
      slugify(`${sourceClassLevelName}-to-${targetClassLevelName}`, `progression-point-${index}`)
    );
    const scopeStatus = String(preflight?.scopeStatus || "").toLowerCase();
    const selected = point?.selected ?? point?.inScope ?? (
      scopeStatus === "known" || expectedClassroomCount !== ""
    );

    return {
      ...point,
      id,
      sourceClassLevelName,
      targetClassLevelName,
      expectedClassroomCount,
      confirmedCount,
      confirmedClassrooms,
      draftClassrooms,
      currentDraft,
      availableChildCount: Math.max(
        0,
        Number(
          point?.availableChildCount ??
          point?.availableChildrenCount ??
          point?.remainingChildCount ??
          point?.eligibleChildCount ??
          0
        ) || 0
      ),
      reconciliationStatus: String(point?.reconciliationStatus || "pending"),
      reconciliationVersion: Math.max(0, Number(point?.reconciliationVersion || 0)),
      persistedExpectedCount: expectedClassroomCount !== "",
      selected: Boolean(selected)
    };
  });
};

const hasValidExpectedCount = (point) =>
  Boolean(point) &&
  Number.isInteger(Number(point.expectedClassroomCount)) &&
  Number(point.expectedClassroomCount) >= 1;

const getStartedClassroomCount = (point) => {
  const draftIds = new Set(
    (point?.draftClassrooms || [])
      .map((draft) => draft?.id || draft?._id || draft?.draftId)
      .filter(Boolean)
  );
  const unidentifiedDraftCount = (point?.draftClassrooms || []).filter(
    (draft) => !(draft?.id || draft?._id || draft?.draftId)
  ).length;
  const currentDraftId = point?.currentDraft?.id ||
    point?.currentDraft?._id ||
    point?.currentDraft?.draftId;
  const currentDraftCount = point?.currentDraft &&
    (!currentDraftId || !draftIds.has(currentDraftId))
    ? 1
    : 0;

  return Number(point?.confirmedCount || 0) +
    draftIds.size +
    unidentifiedDraftCount +
    currentDraftCount;
};

const getRemainingClassroomCount = (point) => Math.max(
  0,
  Number(point.expectedClassroomCount || 0) - Number(point.confirmedCount || 0)
);

const isClassroomBuildComplete = (point) =>
  hasValidExpectedCount(point) && getRemainingClassroomCount(point) === 0;

const isProgressionPointReconciled = (point) =>
  isClassroomBuildComplete(point) && point?.reconciliationStatus === "completed";

const getScopeMode = (progressionPoints) => {
  const selectedPoints = progressionPoints.filter((point) => point.selected);
  const hasCompleteScope = selectedPoints.length > 0 && selectedPoints.every(hasValidExpectedCount);

  return hasCompleteScope ? "known" : "missing";
};

const getCountQuestionClassLevel = (name) =>
  String(name || "Class level").replace(/\bInfants\b/g, "Infant");

function TeacherClassroomTransitionModal({
  show,
  handleClose,
  schoolid,
  teacherid = "",
  currentTeacherName = "",
  currentTeacherEmail = "",
  launchContext = {},
  onReviewClassrooms = null,
  onAssignScreener = null,
  onContinue = null,
  onPopulationRoute = null,
  onUploadClassList = null,
  onSelectPreviousChildren = null,
  onReviewRoster = null
}) {
  const [screen, setScreen] = useState("scope");
  const [transitionState, setTransitionState] = useState("loading");
  const [transitionDetails, setTransitionDetails] = useState({});
  const [classLevels, setClassLevels] = useState([]);
  const [progressionPoints, setProgressionPoints] = useState([]);
  const [scopeMode, setScopeMode] = useState("loading");
  const [selectedProgressionPointId, setSelectedProgressionPointId] = useState("");
  const [scopeError, setScopeError] = useState("");
  const [isSavingScope, setIsSavingScope] = useState(false);
  const [scopeEditSnapshot, setScopeEditSnapshot] = useState(null);
  const [existingClasses, setExistingClasses] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [isLoadingBuilderData, setIsLoadingBuilderData] = useState(false);
  const [builderDataError, setBuilderDataError] = useState("");
  const [classroomName, setClassroomName] = useState("");
  const [classroomNameTouched, setClassroomNameTouched] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [newTeacherEmail, setNewTeacherEmail] = useState("");
  const [teacherMenuOpen, setTeacherMenuOpen] = useState(false);
  const [builderError, setBuilderError] = useState("");
  const [populationRouteContext, setPopulationRouteContext] = useState(null);
  const [reviewContext, setReviewContext] = useState(null);

  const requestedContext = useMemo(
    () => ({
      schoolId: launchContext.schoolId || schoolid || "",
      targetAcademicYear: launchContext.targetAcademicYear || getClassroomTransitionAcademicYear(),
      source: launchContext.source || "",
      launchId: launchContext.launchId || ""
    }),
    [launchContext.launchId, launchContext.schoolId, launchContext.source, launchContext.targetAcademicYear, schoolid]
  );

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    setScreen("scope");
    setTransitionState("loading");
    setTransitionDetails({});
    setClassLevels([]);
    setProgressionPoints([]);
    setScopeMode("loading");
    setSelectedProgressionPointId("");
    setScopeError("");
    setIsSavingScope(false);
    setScopeEditSnapshot(null);
    setExistingClasses([]);
    setTeacherOptions([]);
    setIsLoadingBuilderData(false);
    setBuilderDataError("");
    setClassroomName("");
    setClassroomNameTouched(false);
    setTeacherSearch("");
    setSelectedTeacher(null);
    setNewTeacherEmail("");
    setTeacherMenuOpen(false);
    setBuilderError("");
    setPopulationRouteContext(null);
    setReviewContext(null);

    const requestedSchoolId = String(launchContext.schoolId || "").trim();
    const currentSchoolId = String(schoolid || "").trim();
    if (!currentSchoolId || (requestedSchoolId && requestedSchoolId !== currentSchoolId)) {
      setTransitionState("unauthorized");
      return undefined;
    }

    let cancelled = false;
    const developmentMockRequest = getClassroomTransitionDevelopmentMock();
    const preflightRequest = developmentMockRequest ||
      getClassroomTransitionPreflight(requestedContext);

    preflightRequest
      .then((preflight) => {
        if (cancelled || developmentMockRequest || preflight?.access !== "unclaimed") {
          return preflight;
        }

        return claimClassroomTransitionOwnership(requestedContext);
      })
      .then((preflight) => {
        if (cancelled) {
          return;
        }

        const accessDenied = preflight?.access && preflight.access !== "owner";
        if (accessDenied || !ALLOWED_STATES.has(preflight?.status)) {
          setTransitionState(
            accessDenied || [401, 403].includes(preflight?.statusCode)
              ? "unauthorized"
              : "error"
          );
          setClassLevels([]);
          setProgressionPoints([]);
          return;
        }

        setTransitionDetails(preflight);
        const nextProgressionPoints = normaliseProgressionPoints(preflight);
        setClassLevels(normaliseClassLevels(
          Array.isArray(preflight?.classLevels) && preflight.classLevels.length > 0
            ? preflight.classLevels
            : nextProgressionPoints
        ));
        const nextScopeMode = getScopeMode(nextProgressionPoints);
        const selectedPoints = nextProgressionPoints.filter((point) => point.selected);
        setProgressionPoints(nextProgressionPoints);
        setScopeMode(nextScopeMode);
        const requestedActivePoint = selectedPoints.find(
          (point) => point.id === String(preflight?.activeProgressionPointId ?? "")
        );
        const nextActivePoint = requestedActivePoint &&
          !isProgressionPointReconciled(requestedActivePoint)
          ? requestedActivePoint
          : selectedPoints.find((point) => !isProgressionPointReconciled(point)) ||
            requestedActivePoint ||
            selectedPoints[0];
        setSelectedProgressionPointId(String(nextActivePoint?.id || ""));
        setTransitionState(preflight.status);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setTransitionState([401, 403].includes(error?.response?.status) ? "unauthorized" : "error");
        setScopeMode("loading");
      });

    return () => {
      cancelled = true;
    };
  }, [launchContext.schoolId, requestedContext, schoolid, show]);

  useEffect(() => {
    if (
      !show ||
      !["builder", "upload", "manual", "review"].includes(screen) ||
      transitionState !== "eligible"
    ) {
      return undefined;
    }

    let cancelled = false;
    const currentTeacher = teacherid || currentTeacherName || currentTeacherEmail
      ? {
          teacherid,
          name: currentTeacherName,
          email: currentTeacherEmail,
          status: "active"
        }
      : null;
    const embeddedClasses = getClassroomTransitionRows(transitionDetails, ["classes", "existingClasses"]);
    const embeddedTeachers = getClassroomTransitionRows(transitionDetails, [
      "teachers",
      "activeTeachers",
      "pendingTeachers"
    ]);

    setIsLoadingBuilderData(true);
    setBuilderDataError("");

    const referenceRequest = isClassroomTransitionDevelopmentMock()
      ? Promise.resolve([embeddedClasses, embeddedTeachers, []])
      : Promise.all([
          getClassesBySchoolId(requestedContext.schoolId),
          getTeachersBySchoolId(requestedContext.schoolId),
          getTeacherAccountLookup(requestedContext.schoolId)
        ]);

    referenceRequest
      .then(([classesResponse, teachersResponse, lookupResponse]) => {
        if (cancelled) return;

        const classes = getClassroomTransitionRows(classesResponse, ["classes", "rows"]);
        const teachers = mergeTeacherRows(
          currentTeacher ? [currentTeacher] : [],
          getClassroomTransitionRows(teachersResponse, ["teachers", "rows"]),
          getClassroomTransitionRows(lookupResponse, [
            "teachers",
            "rows",
            "accounts",
            "activeTeachers",
            "pendingTeachers",
            "invitedTeachers",
            "pending",
            "invites"
          ])
        );
        const suggestedTeacherId = firstPresentValue(
          transitionDetails?.suggestedTeacherId,
          transitionDetails?.teacherId,
          teacherid
        );
        const suggestedTeacher = teachers.find((teacher) =>
          [teacher.id, teacher.teacherId, teacher.pendingTeacherId].includes(suggestedTeacherId)
        ) || teachers.find((teacher) =>
          normaliseComparableText(teacher.email) === normaliseComparableText(currentTeacherEmail)
        ) || null;

        setExistingClasses(classes);
        setTeacherOptions(teachers);
        if (suggestedTeacher && screen === "builder") {
          setSelectedTeacher(suggestedTeacher);
          setTeacherSearch(suggestedTeacher.name);
        }
        if (!classes.length && !teachers.length) {
          setBuilderDataError(
            "Existing classrooms and teachers could not be checked. Close and try again."
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBuilderDataError(
            "Existing classrooms and teachers could not be checked. Close and try again."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBuilderData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentTeacherEmail,
    currentTeacherName,
    requestedContext.schoolId,
    screen,
    show,
    teacherid,
    transitionDetails,
    transitionState
  ]);

  const isCompleted = transitionState === "completed";
  const selectedProgressionPoints = progressionPoints.filter((point) => point.selected);
  const expectedClassroomTotal = selectedProgressionPoints.reduce(
    (total, point) => total + Number(point.expectedClassroomCount || 0),
    0
  );
  const confirmedClassroomTotal = selectedProgressionPoints.reduce(
    (total, point) => total + Math.min(
      Number(point.expectedClassroomCount || 0),
      Number(point.confirmedCount || 0)
    ),
    0
  );
  const confirmedClassrooms = selectedProgressionPoints.flatMap(
    (point) => point.confirmedClassrooms || []
  );
  const confirmedClassroomIds = Array.from(new Set(
    confirmedClassrooms
      .map((classroom) => classroom?.id || classroom?._id)
      .filter(Boolean)
  ));
  const isPartialProgress = transitionState === "eligible" && scopeMode === "known" && (
    ["in-progress", "reconciliation", "reconciliation-complete"].includes(
      transitionDetails?.progressStatus
    ) ||
    (confirmedClassroomTotal > 0 && confirmedClassroomTotal < expectedClassroomTotal)
  );
  const isEditingScope = Array.isArray(scopeEditSnapshot);
  const canEditScope = transitionState === "eligible" &&
    scopeMode === "known";
  const title = screen !== "scope"
    ? "Classroom Builder"
    : isCompleted || isPartialProgress
      ? "Classroom Transition"
      : "Create classrooms from previous year";

  const updateProgressionPoint = (progressionPointId, changes) => {
    setScopeError("");
    setProgressionPoints((currentPoints) =>
      currentPoints.map((point) =>
        point.id === progressionPointId ? { ...point, ...changes } : point
      )
    );
  };

  const beginScopeEdit = () => {
    if (!canEditScope) return;

    setScopeEditSnapshot(progressionPoints.map((point) => ({ ...point })));
    setScopeError("");
  };

  const cancelScopeEdit = () => {
    if (scopeEditSnapshot) {
      const restoredPoints = scopeEditSnapshot.map((point) => ({ ...point }));
      const selectedPoints = restoredPoints.filter((point) => point.selected);
      const restoredActivePoint = selectedPoints.find(
        (point) => point.id === selectedProgressionPointId
      ) || selectedPoints[0];

      setProgressionPoints(restoredPoints);
      setSelectedProgressionPointId(restoredActivePoint?.id || "");
    }
    setScopeEditSnapshot(null);
    setScopeError("");
  };

  const handleBuilderBack = () => {
    setClassroomName("");
    setClassroomNameTouched(false);
    setTeacherSearch("");
    setSelectedTeacher(null);
    setNewTeacherEmail("");
    setTeacherMenuOpen(false);
    setBuilderError("");
    setPopulationRouteContext(null);
    setReviewContext(null);
    setScreen("scope");
  };

  const activeProgressionPoint = progressionPoints.find(
    (point) => point.id === selectedProgressionPointId
  ) || null;
  const trimmedClassroomName = normaliseRosterName(classroomName);
  const filteredTeacherOptions = useMemo(() => {
    const query = normaliseComparableText(teacherSearch);
    if (!query) return teacherOptions;

    return teacherOptions.filter((teacher) =>
      [teacher.name, teacher.email].some((value) =>
        normaliseComparableText(value).includes(query)
      )
    );
  }, [teacherOptions, teacherSearch]);
  const hasExistingClassName = Boolean(trimmedClassroomName) && existingClasses.some(
    (classItem) => {
      const academicYear = getClassAcademicYear(classItem);
      return normaliseComparableText(getClassName(classItem)) ===
        normaliseComparableText(trimmedClassroomName) &&
        (!academicYear || academicYear === requestedContext.targetAcademicYear);
    }
  );
  const transitionDrafts = [
    ...getClassroomTransitionRows(transitionDetails, ["classroomDrafts", "draftClassrooms"]),
    ...progressionPoints.flatMap((point) => point.draftClassrooms || [])
  ];
  const hasConflictingDraftName = Boolean(trimmedClassroomName) && transitionDrafts.some(
    (draft) => normaliseComparableText(getClassName(draft)) ===
      normaliseComparableText(trimmedClassroomName)
  );
  const classroomNameError = !classroomNameTouched
    ? ""
    : !trimmedClassroomName
      ? "Enter a new classroom name."
      : hasExistingClassName
        ? `A class with this name already exists for ${requestedContext.targetAcademicYear}.`
        : hasConflictingDraftName
          ? "This name is already being used in another Classroom Transition draft."
          : "";
  const showNewTeacherFields = Boolean(
    normaliseRosterName(teacherSearch) && !selectedTeacher && filteredTeacherOptions.length === 0
  );
  const teacherIntent = selectedTeacher
    ? {
        kind: selectedTeacher.kind,
        teacherId: selectedTeacher.kind === "active" ? selectedTeacher.teacherId : undefined,
        pendingTeacherId: selectedTeacher.kind === "pending"
          ? selectedTeacher.pendingTeacherId
          : undefined,
        name: selectedTeacher.name,
        email: selectedTeacher.email
      }
    : showNewTeacherFields && isValidEmail(newTeacherEmail)
      ? {
          kind: "pending-invite",
          name: normaliseRosterName(teacherSearch),
          email: normaliseRosterName(newTeacherEmail)
        }
      : null;

  const openClassroomBuilder = (progressionPoint) => {
    if (!progressionPoint) return;

    setSelectedProgressionPointId(progressionPoint.id);
    setScreen("builder");
    setBuilderError("");
    onContinue?.(progressionPoint);
  };

  const resumeProgressionPoint = (progressionPoint) => {
    if (!progressionPoint || isProgressionPointReconciled(progressionPoint)) return;

    if (isClassroomBuildComplete(progressionPoint)) {
      setSelectedProgressionPointId(progressionPoint.id);
      setScreen("reconciliation");
      setBuilderError("");
      onContinue?.(progressionPoint);
      return;
    }

    const draft = progressionPoint.currentDraft;
    if (!draft || !["manual", "upload"].includes(draft.method)) {
      openClassroomBuilder(progressionPoint);
      return;
    }

    const restoredTeacher = draft.teacherIntent
      ? {
          ...draft.teacherIntent,
          id: firstPresentValue(
            draft.teacherIntent.teacherId,
            draft.teacherIntent.pendingTeacherId,
            draft.teacherIntent.email
          )
        }
      : null;
    const routeContext = {
      method: draft.method,
      schoolId: requestedContext.schoolId,
      targetAcademicYear: requestedContext.targetAcademicYear,
      progressionPointId: progressionPoint.id,
      classLevelName: progressionPoint.targetClassLevelName,
      classroomName: draft.classroomName || draft.name || "",
      teacherIntent: draft.teacherIntent
    };

    setSelectedProgressionPointId(progressionPoint.id);
    setClassroomName(routeContext.classroomName);
    setClassroomNameTouched(false);
    setSelectedTeacher(restoredTeacher);
    setTeacherSearch(restoredTeacher?.name || draft.teacherIntent?.name || "");
    setNewTeacherEmail(
      draft.teacherIntent?.kind === "pending-invite" ? draft.teacherIntent.email || "" : ""
    );
    setBuilderError("");
    setPopulationRouteContext(routeContext);
    setScreen(draft.method);
    onContinue?.(progressionPoint);
  };

  const selectBuilderProgressionPoint = (progressionPointId) => {
    const point = progressionPoints.find((item) => item.id === progressionPointId);
    setSelectedProgressionPointId(progressionPointId);
    setClassroomName("");
    setClassroomNameTouched(false);
    setBuilderError("");
    if (point && isClassroomBuildComplete(point) && !isProgressionPointReconciled(point)) {
      setScreen("reconciliation");
    }
  };

  const handleTeacherSearchChange = (event) => {
    setTeacherSearch(event.target.value);
    setSelectedTeacher(null);
    setNewTeacherEmail("");
    setTeacherMenuOpen(true);
    setBuilderError("");
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherSearch(teacher.name);
    setNewTeacherEmail("");
    setTeacherMenuOpen(false);
    setBuilderError("");
  };

  const handlePopulationRoute = (method) => {
    setClassroomNameTouched(true);
    setBuilderError("");

    if (!trimmedClassroomName || hasExistingClassName || hasConflictingDraftName) return;
    if (builderDataError) {
      setBuilderError(builderDataError);
      return;
    }
    if (!teacherIntent) {
      setBuilderError(
        showNewTeacherFields
          ? "Enter a valid email for the new teacher."
          : "Choose an existing teacher or enter a new teacher."
      );
      return;
    }
    if (!activeProgressionPoint) return;

    const routeContext = {
      method,
      schoolId: requestedContext.schoolId,
      targetAcademicYear: requestedContext.targetAcademicYear,
      progressionPointId: activeProgressionPoint.id,
      classLevelName: activeProgressionPoint.targetClassLevelName,
      classroomName: trimmedClassroomName,
      teacherIntent
    };

    onPopulationRoute?.(routeContext);
    if (method === "upload") onUploadClassList?.(routeContext);
    if (method === "manual") onSelectPreviousChildren?.(routeContext);
    setPopulationRouteContext(routeContext);
    setScreen(method);
  };

  const handleKnownScopeContinue = () => {
    const selectedProgressionPoint = progressionPoints.find(
      (point) => point.id === selectedProgressionPointId
    );

    if (selectedProgressionPoint) {
      resumeProgressionPoint(selectedProgressionPoint);
    }
  };

  const handleReviewRoster = (review) => {
    const nextReview = {
      ...review,
      confirmedCount: activeProgressionPoint?.confirmedCount || 0
    };
    setReviewContext(nextReview);
    setScreen("review");
    onReviewRoster?.(nextReview);
  };

  const handleClassroomConfirmed = ({ response, review }) => {
    const confirmedClassroom = response.classroom || response.confirmedClassroom;
    const committedProgress = response.progressionPoint;

    setProgressionPoints((currentPoints) => currentPoints.map((point) => {
      if (point.id !== review.progressionPointId) return point;
      const confirmedClassrooms = point.confirmedClassrooms.some((classItem) =>
        (classItem.id || classItem._id) === confirmedClassroom.id
      )
        ? point.confirmedClassrooms
        : [...point.confirmedClassrooms, confirmedClassroom];

      return {
        ...point,
        confirmedCount: Number(committedProgress.confirmedCount),
        confirmedClassrooms,
        draftClassrooms: point.draftClassrooms.filter((draft) =>
          (draft.id || draft._id || draft.draftId) !== review.draftId
        ),
        availableChildCount: Number.isFinite(Number(committedProgress.availableChildCount))
          ? Number(committedProgress.availableChildCount)
          : point.availableChildCount
      };
    }));
    setExistingClasses((currentClasses) => [...currentClasses, confirmedClassroom]);
    setClassroomName("");
    setClassroomNameTouched(false);
    setBuilderError("");
    setPopulationRouteContext(null);
    setReviewContext(null);
    setScreen(
      Number(committedProgress.confirmedCount) >= Number(committedProgress.expectedClassroomCount)
        ? "reconciliation"
        : "builder"
    );
  };

  const handleReconciliationCompleted = (response) => {
    const completedPointId = response?.progressionPoint?.id || activeProgressionPoint?.id;
    const nextPointId = response?.nextProgressionPointId;
    setProgressionPoints((currentPoints) => currentPoints.map((point) =>
      point.id === completedPointId
        ? {
            ...point,
            reconciliationStatus: "completed",
            reconciliationVersion: Number(
              response?.progressionPoint?.reconciliationVersion ||
              point.reconciliationVersion + 1
            ),
            availableChildCount: 0
          }
        : point
    ));

    if (nextPointId) {
      setSelectedProgressionPointId(String(nextPointId));
      setScreen(response?.nextStep === "reconciliation" ? "reconciliation" : "builder");
    } else {
      setScreen("reconciliation-complete");
    }
  };

  const handleMissingScopeContinue = async () => {
    const selectedPoints = progressionPoints.filter((point) => point.selected);

    if (selectedPoints.length === 0) {
      setScopeError("Select at least one class level.");
      return;
    }
    if (!selectedPoints.every(hasValidExpectedCount)) {
      setScopeError("Enter a whole number of at least 1 for each selected class level.");
      return;
    }
    if (selectedPoints.some((point) =>
      Number(point.expectedClassroomCount) < getStartedClassroomCount(point)
    )) {
      setScopeError("A classroom count cannot be lower than classrooms already started.");
      return;
    }

    const scopeRequest = {
      schoolId: requestedContext.schoolId,
      targetAcademicYear: requestedContext.targetAcademicYear,
      expectedScopeVersion: Number(transitionDetails?.scopeVersion ?? 0),
      progressionPoints: selectedPoints.map((point) => ({
        progressionPointId: point.id,
        expectedClassroomCount: Number(point.expectedClassroomCount)
      }))
    };

    setIsSavingScope(true);
    setScopeError("");
    try {
      const savedScope = isClassroomTransitionDevelopmentMock()
        ? {
            scopeStatus: "known",
            progressionPoints: selectedPoints.map((point) => ({
              ...point,
              expectedClassroomCount: Number(point.expectedClassroomCount)
            }))
          }
        : await saveClassroomTransitionScope(scopeRequest);
      const savedPoints = normaliseProgressionPoints({
        scopeStatus: "known",
        progressionPoints: savedScope?.progressionPoints || selectedPoints
      });
      const activeProgressionPoint = savedPoints.find(
        (point) => point.selected && point.id === savedScope?.activeProgressionPointId
      ) || savedPoints.find((point) => point.selected);

      setProgressionPoints(savedPoints);
      setTransitionDetails((current) => ({
        ...current,
        ...savedScope,
        targetAcademicYear: savedScope?.targetAcademicYear || current.targetAcademicYear
      }));
      setSelectedProgressionPointId(activeProgressionPoint?.id || "");
      setScopeMode("known");
      setScopeEditSnapshot(null);
      if (activeProgressionPoint) {
        openClassroomBuilder(activeProgressionPoint);
      }
    } catch (error) {
      setScopeError(
        error?.response?.status === 409
          ? "This transition scope changed in another session. Close and reopen to load the latest values."
          : "Classroom Transition scope could not be saved. Please try again."
      );
    } finally {
      setIsSavingScope(false);
    }
  };

  const renderClassroomBuilder = () => {
    if (!activeProgressionPoint) {
      return (
        <Alert variant="danger" className="mb-0">
          Choose a class level before opening the Classroom Builder.
        </Alert>
      );
    }

    const confirmedCount = Math.min(
      Number(activeProgressionPoint.expectedClassroomCount),
      activeProgressionPoint.confirmedCount
    );
    const expectedCount = Number(activeProgressionPoint.expectedClassroomCount);
    const remainingCount = Math.max(0, expectedCount - confirmedCount);
    const confirmedNames = activeProgressionPoint.confirmedClassrooms
      .map(getClassName)
      .filter(Boolean)
      .join(", ");

    return (
      <div className="classroom-builder">
        <div className="classroom-builder-tabs" role="tablist" aria-label="Class levels">
          {progressionPoints.filter((point) => point.selected).map((point) => (
            <button
              type="button"
              role="tab"
              aria-selected={point.id === activeProgressionPoint.id}
              aria-label={`${point.targetClassLevelName}, ${point.confirmedCount} of ${point.expectedClassroomCount} classrooms set up`}
              className={point.id === activeProgressionPoint.id ? "active" : ""}
              key={point.id}
              disabled={screen !== "builder"}
              onClick={() => selectBuilderProgressionPoint(point.id)}
            >
              <span>{point.targetClassLevelName}</span>
              <small>{point.confirmedCount}/{point.expectedClassroomCount}</small>
            </button>
          ))}
        </div>

        <section className="classroom-builder-progress" aria-labelledby="classroom-builder-progress-heading">
          <div className="classroom-builder-progress-copy">
            <strong id="classroom-builder-progress-heading">
              {activeProgressionPoint.targetClassLevelName} — {confirmedCount} of {expectedCount} classrooms set up
            </strong>
            <div
              className="classroom-builder-progress-track"
              role="progressbar"
              aria-label={`${activeProgressionPoint.targetClassLevelName} classroom progress`}
              aria-valuemin="0"
              aria-valuemax={expectedCount}
              aria-valuenow={confirmedCount}
            >
              <span style={{ width: `${expectedCount ? (confirmedCount / expectedCount) * 100 : 0}%` }} />
            </div>
          </div>
          <div className="classroom-builder-progress-summary">
            {confirmedNames && (
              <span className="classroom-builder-confirmed-name">
                <FaCheck aria-hidden="true" /> {confirmedNames}
              </span>
            )}
            <span>{remainingCount} remaining</span>
          </div>
        </section>

        {screen === "reconciliation" ? (
          <TeacherClassroomReconciliation
            progressionPoint={activeProgressionPoint}
            schoolId={requestedContext.schoolId}
            targetAcademicYear={requestedContext.targetAcademicYear}
            developmentMock={isClassroomTransitionDevelopmentMock()}
            onBack={() => setScreen("builder")}
            onCompleted={handleReconciliationCompleted}
          />
        ) : screen === "reconciliation-complete" ? (
          <section className="classroom-transition-complete-handoff">
            <Alert variant="success" className="mb-0">
              All affected class levels have been reconciled. Every previous-year child has been accounted for.
            </Alert>
            {onAssignScreener && confirmedClassroomIds.length > 0 && (
              <Button
                variant="primary"
                onClick={() => onAssignScreener({
                  classIds: confirmedClassroomIds,
                  classNames: confirmedClassrooms.map(getClassName).filter(Boolean),
                  targetAcademicYear: requestedContext.targetAcademicYear
                })}
              >
                Assign screeners to classrooms
              </Button>
            )}
          </section>
        ) : screen === "builder" && remainingCount === 0 ? (
          <Alert variant="success" className="mb-0">
            All expected classrooms for {activeProgressionPoint.targetClassLevelName} are set up.
            Reopen this class level to reconcile the remaining children.
          </Alert>
        ) : screen === "builder" ? (
        <section className="classroom-builder-card" aria-label="New classroom details">
          {isLoadingBuilderData && (
            <div className="classroom-transition-loading classroom-builder-loading" role="status">
              <Spinner animation="border" size="sm" />
              <span>Checking classrooms and teachers…</span>
            </div>
          )}
          {builderDataError && <Alert variant="warning">{builderDataError}</Alert>}

          <div className="classroom-builder-fields">
            <div className="classroom-builder-field">
              <label htmlFor="classroom-builder-name">
                New classroom name <span aria-hidden="true">*</span>
              </label>
              <input
                id="classroom-builder-name"
                type="text"
                required
                aria-label="New classroom name"
                value={classroomName}
                aria-invalid={Boolean(classroomNameError)}
                aria-describedby={classroomNameError ? "classroom-builder-name-error" : undefined}
                onBlur={() => setClassroomNameTouched(true)}
                onChange={(event) => {
                  setClassroomName(event.target.value);
                  setClassroomNameTouched(false);
                  setBuilderError("");
                }}
              />
              {classroomNameError && (
                <span className="classroom-builder-field-error" id="classroom-builder-name-error">
                  {classroomNameError}
                </span>
              )}
            </div>

            <div className="classroom-builder-field classroom-builder-teacher-field">
              <label htmlFor="classroom-builder-teacher">
                Classroom teacher <span aria-hidden="true">*</span>
              </label>
              <div className="classroom-builder-combobox">
                <FaUser aria-hidden="true" />
                <input
                  id="classroom-builder-teacher"
                  type="text"
                  role="combobox"
                  aria-label="Classroom teacher"
                  aria-autocomplete="list"
                  aria-controls="classroom-builder-teacher-options"
                  aria-expanded={teacherMenuOpen}
                  value={teacherSearch}
                  onFocus={() => setTeacherMenuOpen(true)}
                  onChange={handleTeacherSearchChange}
                />
              </div>
              {teacherMenuOpen && filteredTeacherOptions.length > 0 && (
                <div
                  id="classroom-builder-teacher-options"
                  className="classroom-builder-teacher-options"
                  role="listbox"
                  aria-label="Matching teachers"
                >
                  {filteredTeacherOptions.map((teacher) => (
                    <button
                      type="button"
                      role="option"
                      aria-selected={selectedTeacher?.id === teacher.id}
                      key={teacher.id}
                      onClick={() => handleTeacherSelect(teacher)}
                    >
                      <strong>{teacher.name}</strong>
                      <span>{teacher.email}</span>
                      <small>{teacher.kind === "pending" ? "Invitation pending" : "Active teacher"}</small>
                    </button>
                  ))}
                </div>
              )}
              {selectedTeacher?.kind === "active" && (
                <span className="classroom-builder-teacher-status success">
                  <FaCheck aria-hidden="true" /> Matched to existing teacher
                </span>
              )}
              {selectedTeacher?.kind === "pending" && (
                <span className="classroom-builder-teacher-status pending">
                  Invitation pending — ownership will be assigned after activation
                </span>
              )}
              {showNewTeacherFields && (
                <div className="classroom-builder-new-teacher">
                  <label htmlFor="classroom-builder-new-teacher-email">New teacher email</label>
                  <input
                    id="classroom-builder-new-teacher-email"
                    type="email"
                    value={newTeacherEmail}
                    onChange={(event) => {
                      setNewTeacherEmail(event.target.value);
                      setBuilderError("");
                    }}
                  />
                  <span>
                    Invitation intent will be carried into classroom review. Ownership is not assigned until activation.
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="classroom-builder-availability">
            <span className="classroom-builder-availability-icon" aria-hidden="true">
              <FaUsers />
            </span>
            <strong>
              {activeProgressionPoint.availableChildCount} children available from last year
            </strong>
          </div>

          <div className="classroom-builder-route-heading">
            <h3>How would you like to add children to this classroom?</h3>
            <p>You can upload a class list file or select children manually from last year’s group.</p>
          </div>
          {builderError && <Alert variant="danger">{builderError}</Alert>}
          <div className="classroom-builder-routes">
            <button
              type="button"
              className="classroom-builder-route upload"
              aria-label="Upload class list"
              onClick={() => handlePopulationRoute("upload")}
            >
              <span className="classroom-builder-route-icon" aria-hidden="true">
                <FaCloudUploadAlt />
              </span>
              <span className="classroom-builder-route-copy">
                <strong>
                  Upload class list <small>Recommended</small>
                </strong>
                <span>
                  Upload a file containing the children for this new classroom.
                  We’ll match them with last year’s records where possible.
                </span>
              </span>
              <FaArrowRight aria-hidden="true" />
            </button>
            <button
              type="button"
              className="classroom-builder-route manual"
              aria-label="Select from last year’s children"
              onClick={() => handlePopulationRoute("manual")}
            >
              <span className="classroom-builder-route-icon" aria-hidden="true">
                <FaUsers />
              </span>
              <span className="classroom-builder-route-copy">
                <strong>Select from last year’s children</strong>
                <span>
                  Manually choose children from last year’s group to add to this new classroom.
                </span>
              </span>
              <FaArrowRight aria-hidden="true" />
            </button>
          </div>
        </section>
        ) : screen === "review" && reviewContext ? (
          <TeacherClassroomReviewConfirm
            review={reviewContext}
            teacherOptions={teacherOptions}
            existingClasses={existingClasses}
            developmentMock={isClassroomTransitionDevelopmentMock()}
            onBack={() => setScreen(reviewContext.method)}
            onConfirmed={handleClassroomConfirmed}
          />
        ) : populationRouteContext ? (
          <TeacherClassroomPopulationWorkspace
            mode={screen}
            routeContext={populationRouteContext}
            initialChildren={Array.isArray(activeProgressionPoint.availableChildren)
              ? activeProgressionPoint.availableChildren
              : null}
            developmentMock={isClassroomTransitionDevelopmentMock()}
            onBack={() => setScreen("builder")}
            onReview={handleReviewRoster}
          />
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      className={`teacher-classroom-transition-modal${screen !== "scope" ? " classroom-builder-modal" : ""}`}
      data-transition-state={transitionState}
    >
      <Modal.Header closeButton>
        <Modal.Title as={screen !== "scope" ? "h2" : "div"}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {screen === "scope" && (
          <p className="classroom-transition-intro">
            Create this year’s classrooms using children from last year’s classes.
          </p>
        )}

        {screen === "scope" && transitionState === "loading" && (
          <div className="classroom-transition-loading" role="status">
            <Spinner animation="border" size="sm" />
            <span>Checking Classroom Transition availability…</span>
          </div>
        )}

        {screen === "scope" && transitionState === "eligible" && scopeMode === "known" && !isPartialProgress && !isEditingScope && (
          <fieldset className="classroom-transition-scope-known">
            <legend className="visually-hidden">Choose a class level to set up</legend>
            <div className="classroom-transition-levels">
              {progressionPoints.filter((point) => point.selected).map((point) => (
                <label
                  className={`classroom-transition-level classroom-transition-level-choice${
                    selectedProgressionPointId === point.id ? " selected" : ""
                  }`}
                  key={point.id}
                >
                  <input
                    type="radio"
                    name="classroom-transition-progression-point"
                    aria-label={`${point.targetClassLevelName}, ${point.expectedClassroomCount} classrooms to set up`}
                    checked={selectedProgressionPointId === point.id}
                    disabled={isProgressionPointReconciled(point)}
                    onChange={() => setSelectedProgressionPointId(point.id)}
                  />
                  <span className="classroom-transition-level-icon" aria-hidden="true">
                    <FaPeopleCarry />
                  </span>
                  <span className="classroom-transition-level-name">
                    {point.targetClassLevelName}
                  </span>
                  <span className="classroom-transition-level-count">
                    {point.expectedClassroomCount} classrooms to set up
                  </span>
                  <FaChevronRight aria-hidden="true" />
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {screen === "scope" && isPartialProgress && !isEditingScope && (
          <section
            className="classroom-transition-resume"
            aria-labelledby="classroom-transition-resume-heading"
          >
            <h3 id="classroom-transition-resume-heading" className="visually-hidden">
              Classroom Transition progress
            </h3>
            <div className="classroom-transition-levels">
              {selectedProgressionPoints.map((point) => {
                const complete = isProgressionPointReconciled(point);
                const classroomsComplete = isClassroomBuildComplete(point);
                const confirmedNames = point.confirmedClassrooms
                  .map(getClassName)
                  .filter(Boolean);

                return (
                  <button
                    type="button"
                    className="classroom-transition-level classroom-transition-resume-level"
                    key={point.id}
                    disabled={complete}
                    aria-label={`${point.targetClassLevelName}, ${point.confirmedCount} of ${point.expectedClassroomCount} classrooms set up${
                      confirmedNames.length ? `, completed: ${confirmedNames.join(", ")}` : ""
                    }${classroomsComplete && !complete ? ", reconcile remaining children" : ""}${
                      point.currentDraft ? ", resume saved classroom" : ""
                    }`}
                    onClick={() => resumeProgressionPoint(point)}
                  >
                    <span className="classroom-transition-level-icon" aria-hidden="true">
                      <FaPeopleCarry />
                    </span>
                    <span className="classroom-transition-level-name">
                      {point.targetClassLevelName}
                      {confirmedNames.length > 0 && (
                        <small className="classroom-transition-confirmed-classrooms">
                          <FaCheck aria-hidden="true" /> {confirmedNames.join(", ")}
                        </small>
                      )}
                    </span>
                    <span className="classroom-transition-level-progress">
                      <strong>{point.confirmedCount}/{point.expectedClassroomCount}</strong>
                      <span>classrooms set up</span>
                    </span>
                    {complete ? <FaCheck aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {screen === "scope" && transitionState === "eligible" && (scopeMode === "missing" || isEditingScope) && (
          <fieldset className="classroom-transition-scope-form">
            <legend>Which class levels have been reorganised?</legend>
            <div className="classroom-transition-scope-options">
              {progressionPoints.map((point) => {
                const optionId = `classroom-transition-scope-${point.id}`;
                const countId = `${optionId}-count`;
                const startedClassroomCount = getStartedClassroomCount(point);
                const optionLabel = [point.sourceClassLevelName, point.targetClassLevelName]
                  .filter(Boolean)
                  .join(" → ");

                return (
                  <div className="classroom-transition-scope-option" key={point.id}>
                    <label
                      className={`classroom-transition-scope-check${point.selected ? " selected" : ""}`}
                      htmlFor={optionId}
                    >
                      <input
                        id={optionId}
                        type="checkbox"
                        aria-label={optionLabel.replace(" → ", " to ")}
                        checked={point.selected}
                        disabled={startedClassroomCount > 0 ||
                          (point.persistedExpectedCount && !isEditingScope)}
                        onChange={(event) => updateProgressionPoint(point.id, {
                          selected: event.target.checked
                        })}
                      />
                      <span>{optionLabel || point.targetClassLevelName}</span>
                    </label>
                    {point.selected && (
                      <div className="classroom-transition-count-field">
                        <label htmlFor={countId}>
                          Number of {getCountQuestionClassLevel(point.targetClassLevelName)} classrooms in the school this year
                        </label>
                        <input
                          id={countId}
                          type="number"
                          inputMode="numeric"
                          min={Math.max(1, startedClassroomCount)}
                          step="1"
                          readOnly={point.persistedExpectedCount && !isEditingScope}
                          value={point.expectedClassroomCount}
                          onChange={(event) => updateProgressionPoint(point.id, {
                            expectedClassroomCount: event.target.value
                          })}
                        />
                        {isEditingScope && startedClassroomCount > 0 && (
                          <small className="classroom-transition-scope-protection">
                            This class level has {startedClassroomCount} {startedClassroomCount === 1
                              ? "classroom"
                              : "classrooms"} already started. It cannot be removed or reduced below this number.
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {scopeError && <Alert variant="danger">{scopeError}</Alert>}
          </fieldset>
        )}

        {screen === "scope" && transitionState === "completed" && (
          <section aria-labelledby="classroom-transition-completed-heading">
            <h3 id="classroom-transition-completed-heading">Class levels already set up</h3>
            <div className="classroom-transition-information">
              <FaInfoCircle aria-hidden="true" />
              <p>
                The class levels below have already been set up. You can add or review
                classrooms within these class levels.
              </p>
            </div>
            <div className="classroom-transition-levels">
              {classLevels.map((classLevel) => (
                <button
                  type="button"
                  className="classroom-transition-level"
                  key={classLevel.id}
                  aria-label={`Review ${classLevel.name} classrooms`}
                  onClick={() => onReviewClassrooms?.(classLevel)}
                >
                  <span className="classroom-transition-level-icon" aria-hidden="true">
                    <FaPeopleCarry />
                  </span>
                  <span className="classroom-transition-level-name">{classLevel.name}</span>
                  <span className="classroom-transition-level-progress">
                    <strong>{classLevel.confirmedCount}/{classLevel.expectedCount}</strong>
                    <span>set up</span>
                  </span>
                  <FaChevronRight aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        )}

        {screen === "scope" && transitionState === "ineligible" && (
          <Alert variant="secondary" className="mb-0">
            Classroom Transition is not available for this school and academic year.
          </Alert>
        )}

        {screen === "scope" && transitionState === "unauthorized" && (
          <Alert variant="danger" className="mb-0">
            You do not have permission to manage Classroom Transition for this school.
          </Alert>
        )}

        {screen === "scope" && transitionState === "error" && (
          <Alert variant="danger" className="mb-0">
            Classroom Transition could not be loaded. Please close this window and try again.
          </Alert>
        )}
        {screen !== "scope" && renderClassroomBuilder()}
      </Modal.Body>
      {screen === "scope" && (
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={isEditingScope ? cancelScopeEdit : handleClose}
          >
            {isEditingScope
              ? "Cancel"
              : transitionState === "eligible" && !isPartialProgress ? "Back" : "Cancel"}
          </Button>
          {transitionState === "eligible" && scopeMode === "known" && !isEditingScope && canEditScope && (
            <Button variant="outline-primary" onClick={beginScopeEdit}>
              Edit class levels
            </Button>
          )}
          {transitionState === "eligible" && scopeMode === "known" && !isPartialProgress && !isEditingScope && (
            <Button
              variant="primary"
              disabled={!selectedProgressionPointId}
              onClick={handleKnownScopeContinue}
            >
              Continue
            </Button>
          )}
          {transitionState === "eligible" && (scopeMode === "missing" || isEditingScope) && (
            <Button
              variant="primary"
              disabled={isSavingScope}
              onClick={handleMissingScopeContinue}
            >
              {isSavingScope ? "Saving…" : "Continue"}
            </Button>
          )}
        </Modal.Footer>
      )}
      {screen === "builder" && (
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleBuilderBack}>
            Back
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default TeacherClassroomTransitionModal;
